import classNames from "classnames";
import cuid from "cuid";
import { useFormikContext } from "formik";
import { get, isEmpty, isString } from "lodash";
import PropTypes from "prop-types";
import { ATTACHMENT_TYPES } from "utils/constants";

export const fieldId = (name) => `onboarding-${name.replace(/\./g, "-")}`;

const useField = (name) => {
  const { values, errors, touched, handleChange, handleBlur } =
    useFormikContext();
  return {
    value: get(values, name) || "",
    error: get(touched, name) && get(errors, name),
    handleChange,
    handleBlur,
  };
};

export const Field = ({
  name,
  label,
  required,
  later,
  hint,
  error,
  children,
}) => (
  <div className="onboarding__field">
    <label htmlFor={fieldId(name)}>
      {label}
      {required ? <span className="onboarding__required"> *</span> : null}
    </label>
    {children}
    {error ? <span className="onboarding__error">{error}</span> : null}
    {later ? <span className="onboarding__later">{later}</span> : null}
    {hint ? <span className="onboarding__help">{hint}</span> : null}
  </div>
);

Field.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  later: PropTypes.string,
  hint: PropTypes.string,
  error: PropTypes.string,
  children: PropTypes.node.isRequired,
};

Field.defaultProps = {
  required: false,
  later: null,
  hint: null,
  error: null,
};

export const TextField = ({
  name,
  label,
  required,
  later,
  hint,
  ...inputProps
}) => {
  const { value, error, handleChange, handleBlur } = useField(name);
  return (
    <Field
      name={name}
      label={label}
      required={required}
      error={error || null}
      later={later && isEmpty(value) ? later : null}
      hint={hint}
    >
      <input
        id={fieldId(name)}
        name={name}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        aria-invalid={Boolean(error)}
        className={classNames("onboarding__input", {
          "onboarding__input--invalid": error,
        })}
        {...inputProps}
      />
    </Field>
  );
};

TextField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  later: PropTypes.string,
  hint: PropTypes.string,
};

TextField.defaultProps = {
  required: false,
  later: null,
  hint: null,
};

export const SelectField = ({
  name,
  label,
  required,
  later,
  placeholder,
  options,
}) => {
  const { value, error, handleChange, handleBlur } = useField(name);
  return (
    <Field
      name={name}
      label={label}
      required={required}
      error={error || null}
      later={later && isEmpty(value) ? later : null}
    >
      <select
        id={fieldId(name)}
        name={name}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        aria-invalid={Boolean(error)}
        className={classNames("onboarding__input", {
          "onboarding__input--invalid": error,
        })}
      >
        {placeholder ? <option value="">{placeholder}</option> : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Field>
  );
};

SelectField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  later: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
};

SelectField.defaultProps = {
  required: false,
  later: null,
  placeholder: null,
};

const readFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const Logo = ({ labels }) => {
  const { values, setFieldValue } = useFormikContext();
  const icon = get(values, "icon");
  const preview = isString(icon) ? icon : get(icon, "content");
  const pick = async ({ target }) => {
    const [file] = target.files || [];
    if (!file) return;
    setFieldValue("icon", {
      id: cuid(),
      name: file.name,
      contentType: file.type,
      content: await readFile(file),
      type: ATTACHMENT_TYPES.ICON,
    });
  };
  return (
    <div className="onboarding__logo">
      <span className="onboarding__logo-preview">
        {preview ? (
          <img src={preview} alt="" />
        ) : (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <path d="m17 8-5-5-5 5" />
            <path d="M12 3v12" />
          </svg>
        )}
      </span>
      <span className="onboarding__logo-text">
        <strong>{labels.LOGO}</strong>
        <span>{get(icon, "name") || labels.LOGO_HINT}</span>
      </span>
      <label className="onboarding__button onboarding__button--outline onboarding__file-button">
        {icon ? labels.LOGO_CHANGE : labels.LOGO_PICK}
        <input
          type="file"
          accept="image/png,image/jpeg,image/svg+xml"
          className="onboarding__file"
          onChange={pick}
        />
      </label>
    </div>
  );
};

Logo.propTypes = {
  labels: PropTypes.object.isRequired,
};
